import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Stack,
  FormControl,
  InputLabel,
  OutlinedInput,
} from '@mui/material';
import { Edit, Delete, Close, Add } from '@mui/icons-material';
import { apiService } from '@/services/api';
import { useTranslation } from '@/hooks/useTranslation';

interface Category {
  externalId: string;
  name: string;
}

const SolutionCategoryPage: React.FC = () => {
  const { t } = useTranslation();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [deletingCategory, setDeletingCategory] = useState<Category | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
  });
  const [addFormData, setAddFormData] = useState({
    name: '',
  });

  useEffect(() => {
    const loadCategories = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiService.getSolutionCategories();
        setCategories(response.items);
      } catch (err: any) {
        console.error('Error loading categories:', err);
        setError(err.message || t('solutionCategory.errors.failedToLoad'));
      } finally {
        setLoading(false);
      }
    };

    loadCategories();
  }, []);

  const handleDeleteClick = (category: Category) => {
    setDeletingCategory(category);
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setDeletingCategory(null);
  };

  const handleConfirmDelete = async () => {
    if (!deletingCategory) return;

    try {
      setDeleting(true);
      await apiService.deleteSolutionCategory(deletingCategory.externalId);
      
      // Remove category from list
      setCategories(prev => prev.filter(c => c.externalId !== deletingCategory.externalId));
      
      handleCloseDeleteDialog();
    } catch (err: any) {
      console.error('Error deleting category:', err);
      setError(err.message || t('solutionCategory.errors.failedToDelete'));
    } finally {
      setDeleting(false);
    }
  };

  const handleEditClick = (category: Category) => {
    setEditingCategory(category);
    setEditFormData({
      name: category.name,
    });
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingCategory(null);
    setEditFormData({
      name: '',
    });
  };

  const handleEditInputChange = (field: keyof typeof editFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSaveEdit = async () => {
    if (!editingCategory) return;

    // Validate required fields
    if (!editFormData.name) {
      setError(t('solutionCategory.errors.fillNameField'));
      return;
    }

    try {
      setSaving(true);
      setError(null);
      
      const updatedCategory = await apiService.updateSolutionCategory(
        editingCategory.externalId,
        {
          name: editFormData.name,
        }
      );
      
      // Update category in list
      setCategories(prev => 
        prev.map(c => c.externalId === editingCategory.externalId 
          ? updatedCategory
          : c)
      );

      handleCloseEditModal();
    } catch (err: any) {
      console.error('Error updating category:', err);
      setError(err.message || t('solutionCategory.errors.failedToUpdate'));
    } finally {
      setSaving(false);
    }
  };

  const handleAddClick = () => {
    setAddFormData({
      name: '',
    });
    setIsAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
    setAddFormData({
      name: '',
    });
  };

  const handleAddInputChange = (field: keyof typeof addFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setAddFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSaveAdd = async () => {
    // Validate required fields
    if (!addFormData.name) {
      setError(t('solutionCategory.errors.fillNameField'));
      return;
    }

    try {
      setSaving(true);
      setError(null);
      
      const newCategory = await apiService.createSolutionCategory({
        name: addFormData.name,
      });
      
      // Add category to list
      setCategories(prev => [...prev, newCategory]);

      handleCloseAddModal();
    } catch (err: any) {
      console.error('Error creating category:', err);
      setError(err.message || t('solutionCategory.errors.failedToCreate'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          {t('solutionCategory.title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddClick}
        >
          {t('solutionCategory.addCategory')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Box sx={{ width: '100%', overflowX: 'auto', minWidth: 0 }}>
            <TableContainer component={Paper} variant="outlined">
              <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography variant="h6">{t('solutionCategory.table.name')}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="h6">{t('solutionCategory.table.actions')}</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categories.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2} align="center">
                      <Typography variant="body2" color="text.secondary">
                        {t('solutionCategory.noCategoriesFound')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  categories.map(category => (
                    <TableRow key={category.externalId} hover>
                      <TableCell>{category.name}</TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={1} justifyContent="center">
                          <Tooltip title={t('solutionCategory.tooltips.edit')}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEditClick(category)}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('solutionCategory.tooltips.delete')}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(category)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          </Box>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          {t('solutionCategory.modals.delete.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {t('solutionCategory.modals.delete.confirm', { name: deletingCategory?.name || '' })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="secondary" disabled={deleting}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? t('solutionCategory.modals.delete.deleting') : t('solutionCategory.modals.delete.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Category Modal */}
      <Dialog
        open={isEditModalOpen}
        onClose={handleCloseEditModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6">{t('solutionCategory.modals.edit.title')}</Typography>
            <IconButton onClick={handleCloseEditModal} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>{t('solutionCategory.modals.edit.name')}</InputLabel>
              <OutlinedInput
                value={editFormData.name}
                onChange={handleEditInputChange('name')}
                label={t('solutionCategory.modals.edit.name')}
              />
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseEditModal} color="secondary" disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSaveEdit}
            color="primary"
            variant="contained"
            disabled={saving}
          >
            {saving ? t('solutionCategory.modals.edit.saving') : t('solutionCategory.modals.edit.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Category Modal */}
      <Dialog
        open={isAddModalOpen}
        onClose={handleCloseAddModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6">{t('solutionCategory.modals.add.title')}</Typography>
            <IconButton onClick={handleCloseAddModal} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>{t('solutionCategory.modals.add.name')}</InputLabel>
              <OutlinedInput
                value={addFormData.name}
                onChange={handleAddInputChange('name')}
                label={t('solutionCategory.modals.add.name')}
              />
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseAddModal} color="secondary" disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSaveAdd}
            color="primary"
            variant="contained"
            disabled={saving}
          >
            {saving ? t('solutionCategory.modals.add.creating') : t('solutionCategory.modals.add.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SolutionCategoryPage;

